// Documentação de uso do sistema (premissa do dono, 12/10/2026): TODA tela do menu tem um
// documento .md nesta pasta, exibido no botão "?" do cabeçalho e na Central de ajuda. Toda
// mudança de comportamento visível atualiza o .md correspondente NO MESMO COMMIT (e o campo
// atualizado_em). O teste docs.coverage.test.ts falha se uma tela do menu ficar sem documento.
//
// Formato de cada arquivo:
//   ---
//   titulo: Biblioteca de mídias
//   rota: /media
//   menu: Mídias
//   permissao: media.read
//   ordem: 10
//   atualizado_em: 2026-10-12
//   versao_funcional: 2
//   resumo: Uma frase sobre para que serve a tela.
//   ---
//
// Guias (passo a passo que não é de uma tela — ex.: configurar o acesso às redes) ficam em
// src/docs/guias/ com `tipo: guia` e SEM `rota`; aparecem na Central de ajuda (menu "Guias") e
// abrem por link "/help?doc=./guias/arquivo.md".
//   (Markdown com GFM: títulos, listas, tabelas, citações "> **Dica:** ...")

export interface DocMeta {
  /** "tela" (padrão, ligado a uma rota do menu) ou "guia" (sem tela). */
  tipo: 'tela' | 'guia';
  titulo: string;
  rota: string;
  menu: string;
  permissao?: string;
  ordem: number;
  atualizado_em: string;
  versao_funcional: number;
  resumo: string;
}

export interface Doc extends DocMeta {
  /** Caminho do arquivo (ex.: "./midias/biblioteca.md"). */
  file: string;
  body: string;
}

export const REQUIRED_FIELDS: (keyof DocMeta)[] = ['titulo', 'rota', 'menu', 'atualizado_em', 'versao_funcional', 'resumo'];

/** Lê o cabeçalho "--- chave: valor ---" e devolve metadados + corpo. Erros vão em `problems`. */
export const parseDoc = (file: string, text: string): { doc: Doc; problems: string[] } => {
  const problems: string[] = [];
  const src = (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text).replace(/\r\n/g, '\n');
  const m = /^---\n([\s\S]*?)\n---\n?/.exec(src);
  const meta: Record<string, string> = {};
  if (!m) {
    problems.push(`${file}: sem cabeçalho (--- ... ---)`);
  } else {
    for (const line of m[1].split('\n')) {
      const i = line.indexOf(':');
      if (i <= 0) continue;
      meta[line.slice(0, i).trim()] = line.slice(i + 1).trim();
    }
  }
  const tipo = meta.tipo === 'guia' ? 'guia' : 'tela';
  if (meta.tipo && meta.tipo !== 'guia' && meta.tipo !== 'tela') problems.push(`${file}: tipo deve ser "tela" ou "guia"`);
  for (const f of REQUIRED_FIELDS) {
    if (f === 'rota' && tipo === 'guia') continue;
    if (!meta[f]) problems.push(`${file}: falta "${f}" no cabeçalho`);
  }
  if (tipo === 'guia' && meta.rota) problems.push(`${file}: guia não tem rota (é aberto pela Central de ajuda)`);
  if (meta.atualizado_em && !/^\d{4}-\d{2}-\d{2}$/.test(meta.atualizado_em)) {
    problems.push(`${file}: atualizado_em deve ser AAAA-MM-DD`);
  }
  if (meta.rota && !meta.rota.startsWith('/')) problems.push(`${file}: rota deve começar com /`);
  const doc: Doc = {
    tipo,
    file,
    titulo: meta.titulo ?? file,
    rota: meta.rota ?? '',
    menu: meta.menu ?? 'Outros',
    permissao: meta.permissao || undefined,
    ordem: Number(meta.ordem) || 999,
    atualizado_em: meta.atualizado_em ?? '',
    versao_funcional: Number(meta.versao_funcional) || 1,
    resumo: meta.resumo ?? '',
    body: m ? src.slice(m[0].length).trim() : src.trim(),
  };
  return { doc, problems };
};

/** Documento da tela atual: o de rota mais específica que casa com o caminho. */
export const findDocForPath = (docs: Doc[], pathname: string): Doc | undefined => {
  const path = pathname.replace(/\/+$/, '') || '/';
  let best: Doc | undefined;
  for (const d of docs) {
    if (d.tipo === 'guia') continue;
    const r = d.rota.replace(/\/+$/, '');
    const match = path === r || path.startsWith(`${r}/`);
    if (match && (!best || r.length > best.rota.replace(/\/+$/, '').length)) best = d;
  }
  return best;
};

/** Ordem da Central de ajuda: pela ordem do menu e, dentro dele, pelo campo ordem. */
export const sortDocs = (docs: Doc[], menuOrder: string[]): Doc[] =>
  [...docs].sort((a, b) => {
    const ma = menuOrder.indexOf(a.menu);
    const mb = menuOrder.indexOf(b.menu);
    if (ma !== mb) return (ma < 0 ? 99 : ma) - (mb < 0 ? 99 : mb);
    return a.ordem - b.ordem || a.titulo.localeCompare(b.titulo, 'pt-BR');
  });

/** Busca simples (sem acento, todas as palavras) no título, resumo e corpo. */
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export const searchDocs = (docs: Doc[], query: string): Doc[] => {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return docs;
  return docs.filter((d) => {
    const hay = fold(`${d.titulo} ${d.resumo} ${d.menu} ${d.body}`);
    return words.every((w) => hay.includes(w));
  });
};
